/**
 * utils/ga.js
 *
 * Google Analytics (GA4) via gtag.js.
 *
 * Scope: this util is used only by the TechQC and ReviewPortal screens to track
 * user actions.  The measurement ID is hardcoded below (like the Mixpanel
 * token).  If it is left blank, every function here is a silent no-op, so the
 * app runs unchanged and no events are sent.
 *
 * Usage:
 *   initGA()                                   – once at app startup
 *   identifyGA({ userId, name, role })         – after login
 *   trackGA('event_name', { ...params })       – on a user action
 *   resetGA()                                  – on logout
 */

// GA4 Measurement ID (format: "G-XXXXXXXXXX"). Replace with the real property id.
const GA_MEASUREMENT_ID = "G-KGQCT4JEJD";

let _initialised = false;

/**
 * Loads gtag.js and configures the GA4 property.  Idempotent and safe to call
 * when the measurement ID is absent (no-op).  We disable automatic page_view
 * because the app sends its own screen-view events for the tracked screens.
 */
export function initGA() {
  if (_initialised) return;
  // No-op until a real measurement ID is filled in (avoids loading gtag for the
  // placeholder).
  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === "G-XXXXXXXXXX") return;
  if (typeof window === "undefined" || typeof document === "undefined") return;

  try {
    window.dataLayer = window.dataLayer || [];
    // gtag must push `arguments` (not an array) onto the dataLayer.
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

    _initialised = true;
  } catch (err) {
    // Analytics must never break the app.
    console.warn("[ga] initGA failed:", err.message);
  }
}

/**
 * Associates subsequent events with the logged-in user.
 *
 * @param {{ userId: string|number, name?: string, role?: string }} params
 */
export function identifyGA({ userId, name, role } = {}) {
  if (!window?.gtag || userId == null) return;
  try {
    window.gtag("set", { user_id: String(userId) });
    window.gtag("set", "user_properties", {
      analyst_name: name ?? undefined,
      role: role ?? undefined,
    });
  } catch (err) {
    console.warn("[ga] identifyGA failed:", err.message);
  }
}

/**
 * Sends a GA4 event.  Silent no-op when GA isn't initialised.
 *
 * @param {string} action  event name (snake_case)
 * @param {object} params  event parameters
 */
export function trackGA(action, params = {}) {
  if (!window?.gtag || !action) return;
  try {
    window.gtag("event", action, params);
  } catch (err) {
    console.warn("[ga] trackGA failed:", err.message);
  }
}

/**
 * Clears the associated user on logout.
 */
export function resetGA() {
  if (!window?.gtag) return;
  try {
    window.gtag("set", { user_id: null });
  } catch (err) {
    console.warn("[ga] resetGA failed:", err.message);
  }
}
